import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { getMediaUrl } from '../../services/api';
import './DepartmentsSection.css';

const DepartmentsSection = ({ section, layout = 'default' }) => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      const response = await api.get('/departments/', { params: { page_size: 6 } });
      const deptData = response.data.results || response.data;
      setDepartments(Array.isArray(deptData) ? deptData : []);
    } catch (error) {
      console.error('Error loading departments:', error);
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="departments-section homepage-section">
        <Container>
          <h2 className="section-title">{section.title}</h2>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </section>
    );
  }

  if (departments.length === 0) return null;

  const renderGridLayout = () => (
    <Row className={departments.length < 3 ? 'justify-content-center' : ''}>
      {departments.map(dept => (
        <Col key={dept.id} lg={4} md={6} sm={12} className="d-flex">
          <Card className="department-card">
            {dept.image && (
              <Card.Img 
                variant="top" 
                src={getMediaUrl(dept.image)} 
                alt={dept.name}
              />
            )}
            <Card.Body>
              <Card.Title>
                <Link to={`/departments/${dept.slug}`}>{dept.name}</Link>
              </Card.Title>
              <Card.Text>
                <strong>Head:</strong> {dept.head_officer}
              </Card.Text>
              <Link to={`/departments/${dept.slug}`} className="btn btn-outline-primary btn-sm">
                View Details <i className="fas fa-arrow-right ms-1"></i>
              </Link>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );

  const renderListLayout = () => (
    <div className="department-list">
      {departments.map(dept => (
        <div key={dept.id} className="department-list-item border-bottom py-3">
          <Row>
            {dept.image && (
              <Col md={2} sm={3} xs={4} className="mb-2 mb-md-0">
                <img 
                  src={getMediaUrl(dept.image)} 
                  alt={dept.name}
                  className="img-fluid rounded"
                />
              </Col>
            )}
            <Col md={dept.image ? 10 : 12} sm={9} xs={8}>
              <h5>
                <Link to={`/departments/${dept.slug}`}>{dept.name}</Link>
              </h5>
              <p className="mb-2 text-muted">
                <strong>Head of Department:</strong> {dept.head_officer}
              </p>
              <Link to={`/departments/${dept.slug}`} className="btn btn-sm btn-outline-primary">
                View Details <i className="fas fa-arrow-right ms-1"></i>
              </Link>
            </Col>
          </Row>
        </div>
      ))}
    </div>
  );

  const renderCompactLayout = () => (
    <Row className="g-2">
      {departments.map(dept => (
        <Col key={dept.id} lg={3} md={4} sm={6} xs={6} className="mb-3">
          <Card className="department-card-compact h-100">
            {dept.image && (
              <Card.Img 
                variant="top" 
                src={getMediaUrl(dept.image)} 
                alt={dept.name}
                className="dept-compact-img"
              />
            )}
            <Card.Body className="p-3">
              <h6 className="mb-2">
                <Link to={`/departments/${dept.slug}`}>{dept.name}</Link>
              </h6>
              <p className="small text-muted mb-2">{dept.head_officer}</p>
              <Link to={`/departments/${dept.slug}`} className="btn btn-sm btn-outline-primary w-100">
                View
              </Link>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );

  const renderContent = () => {
    switch(layout) {
      case 'list':
        return renderListLayout();
      case 'compact':
        return renderCompactLayout();
      case 'grid':
      case 'default':
      default:
        return renderGridLayout();
    }
  };

  return (
    <section className={`departments-section homepage-section departments-layout-${layout}`}>
      <Container>
        <h2 className="section-title">{section.title}</h2>
        {renderContent()}
        <div className="text-center mt-4">
          <Link to="/departments" className="btn btn-primary">
            View All Departments <i className="fas fa-arrow-right ms-2"></i>
          </Link>
        </div>
      </Container>
    </section>
  );
};

export default DepartmentsSection;

